import React from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
// import { FolderCreateRequest } from '../../types'; // Using local interface instead
import { useFolderStore } from '../../store/folderStore';
import Modal from '../ui/Modal';
import Input from '../ui/Input';
import Button from '../ui/Button';

interface CreateFolderForm {
  name: string;
  description?: string;
}

const schema = yup.object().shape({
  name: yup
    .string()
    .required('Folder name is required')
    .max(100, 'Folder name must be less than 100 characters'),
  description: yup
    .string()
    .max(500, 'Description must be less than 500 characters')
    .optional(),
}) as yup.ObjectSchema<CreateFolderForm>;

interface CreateFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  parentId?: string;
  parentName?: string;
}

const CreateFolderModal: React.FC<CreateFolderModalProps> = ({
  isOpen,
  onClose,
  parentId,
  parentName,
}) => {
  const { createFolder, isLoading } = useFolderStore();
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CreateFolderForm>({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: CreateFolderForm) => {
    try {
      await createFolder({
        ...data,
        parent_id: parentId,
      });
      handleClose();
    } catch (error) {
      // Error is handled in the store
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={parentName ? `Create Subfolder in "${parentName}"` : 'Create New Folder'}
      maxWidth="md"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input
          {...register('name')}
          label="Folder Name"
          placeholder="Enter folder name"
          error={errors.name?.message}
          autoFocus
        />

        <Input
          {...register('description')}
          label="Description (Optional)"
          placeholder="Brief description of this folder"
          error={errors.description?.message}
        />

        {parentName && (
          <div className="bg-gray-50 p-3 rounded-lg">
            <p className="text-sm text-gray-600">
              This folder will be created inside: <strong>{parentName}</strong>
            </p>
          </div>
        )}

        {!parentName && (
          <p className="text-xs text-gray-500">
            This folder will be available inside <strong>My Data</strong>
          </p>
        )}

        <div className="flex justify-end space-x-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            loading={isLoading}
            disabled={isLoading}
          >
            Create Folder
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateFolderModal;
